// Result workspace shell (Phase 12) — merges the per-result pages (Data / Preview /
// Summary) into one tabbed workspace, mirroring the OrgSettingsLayout + OrgSubnav pattern. Reads
// :resultId, fetches the result metadata once for the shared header (title + "Created by … on …"),
// renders the cross-cutting toolbar (Edit & Re-run / Use Same Filters / Download / Delete).
// Input-file preview hides that toolbar. Summary is an options-menu link for admins.
// Each child renders its body via <Outlet>. Old per-view URLs redirect
// here (see router.tsx).
import { Suspense } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { researchService, resultsService } from '@/services/api';
import {
  filtersToFvForm,
  filtersToStrategicForm,
  filtersToTargetForm,
} from '@/features/research/filtersToForm';
import { beginEditAndRerun, composerPathForSource } from '@/features/research/composerPrefill';
import { stashDraft as stashStrategicDraft } from '@/features/research/strategic/schema';
import { stashDraft as stashTargetDraft } from '@/features/research/target/schema';
import { stashFinancialDraft } from '@/features/research/financial/draft';
import { useResultSummaryMeta } from '@/features/results/useResultSummaryMeta';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useConfirm } from '@/hooks/useConfirm';
import { paths } from '@/routes/paths';
import { formatDateTimeLong } from '@/lib/datetime';
import { useCrumbEntity } from '@/hooks/useCrumbEntity';
import { resultCrumbName, resultHeadingName } from '@/components/layout/breadcrumbs';
import './result-layout.css';

export default function ResultLayout() {
  const { resultId } = useParams<{ resultId: string }>();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const isInputFilePreview = Boolean(searchParams.get('fileId'));
  const isSummary = pathname.endsWith('/summary');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  const { data: summary } = useResultSummaryMeta(resultId);
  useCrumbEntity(
    isInputFilePreview
      ? undefined
      : resultCrumbName(undefined, summary?.resultFilename, summary?.filtersApplied),
  );

  const title = resultHeadingName(undefined, summary?.resultFilename, summary?.filtersApplied);
  const creator = summary?.username || '';
  const createdAt = summary?.createdAt || '';
  const isCreator = !!currentUser && !!summary && String(summary.userId) === String(currentUser.id);
  const version = typeof summary?.version === 'number' ? summary.version : undefined;

  const navigateWithFilters = async (asRerun: boolean) => {
    if (!resultId) return;
    if (asRerun) {
      const seed = (summary?.filtersApplied || {}) as Record<string, unknown>;
      const sourceType = String(seed.source_type ?? seed.sourceType ?? '');
      beginEditAndRerun({
        sourceProcessId: resultId,
        version,
        title,
        filtersSeed: seed,
      });
      navigate(composerPathForSource(sourceType, resultId));
      return;
    }
    try {
      const fromReuse = await researchService.reuseFilters(resultId);
      // Listing/detail filters are camelCase; reuse-filters is snake_case. Merge so
      // Strategic target size / HQ restore regardless of which source holds them.
      const filters = {
        ...((summary?.filtersApplied || {}) as Record<string, unknown>),
        ...fromReuse,
      };
      const sourceType = String(filters.source_type ?? filters.sourceType ?? '');
      if (sourceType === 'strategic_buyer_list') {
        stashStrategicDraft(filtersToStrategicForm(filters));
        toast.info('Re-running with the same filters…');
        navigate(paths.strategic);
      } else if (sourceType === 'financial_verticals') {
        stashFinancialDraft(filtersToFvForm(filters));
        toast.info('Re-running with the same filters…');
        navigate(paths.financialVerticals);
      } else {
        stashTargetDraft(filtersToTargetForm(filters));
        toast.info('Re-running with the same filters…');
        navigate(paths.targetList);
      }
    } catch {
      toast.error('Could not load the filters from this result.');
    }
  };

  const handleUseSameFilters = () => void navigateWithFilters(false);
  const handleEditAndRerun = () => void navigateWithFilters(true);

  const handleDownload = () => {
    if (!resultId) return;
    toast.info('Preparing your download…');
    // Cookie-authed GET that returns the results XLSX with a Content-Disposition
    // attachment header — a plain anchor navigation carries the session cookie and
    // lets the browser save the file using the server-provided filename.
    const a = document.createElement('a');
    a.href = resultsService.downloadUrl(resultId);
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async () => {
    if (!resultId) return;
    const ok = await confirm({
      title: 'Delete result?',
      message: 'Are you sure you want to delete this result?',
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      const res = await resultsService.remove(resultId);
      if (res.success) {
        toast.success(res.message || 'Result deleted.');
        navigate(paths.previousResults);
      } else {
        toast.error(res.message || 'Failed to delete result.');
      }
    } catch {
      toast.error('Failed to delete result.');
    }
  };

  return (
    <div className="container-fluid page-top-padding">
      {isInputFilePreview ? null : (
        <div className="result-workspace-header">
          <div className="result-workspace-title-section">
            <h1 className="result-workspace-title">
              {title}
              {typeof version === 'number' && version > 1 ? (
                <span className="ms-2 badge text-bg-secondary align-middle">v{version}</span>
              ) : null}
            </h1>
            {creator && (
              <p className="result-workspace-meta">
                <span>Created by</span> <span className="fw-semibold">{creator}</span>{' '}
                {createdAt && (
                  <>
                    <span>on</span> <span>{formatDateTimeLong(createdAt)}</span>
                  </>
                )}
              </p>
            )}
          </div>

          {isSummary ? null : (
            <div className="result-workspace-actions">
              <button
                type="button"
                className="btn btn-standard action-btn"
                onClick={handleEditAndRerun}
              >
                <i className="bi bi-pencil-square me-2" />
                Edit &amp; Re-run
              </button>
              <button
                type="button"
                className="btn btn-standard action-btn"
                onClick={handleUseSameFilters}
              >
                <i className="bi bi-sliders me-2" />
                Use settings
              </button>
              <button
                type="button"
                className="btn btn-standard action-btn"
                onClick={handleDownload}
              >
                <i className="bi bi-download me-2" />
                Download CSV
              </button>
              {isCreator && (
                <button
                  type="button"
                  className="btn btn-standard action-btn text-danger"
                  title="Delete"
                  onClick={handleDelete}
                >
                  <i className="bi bi-trash me-2" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Local boundary so a lazy tab body (Data/Summary) suspends here — keeping the header + tab
          row mounted — instead of bubbling to AppLayout's boundary and blanking the whole workspace. */}
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </div>
  );
}
